/**
 * LabelPrinter — orchestrates the docket-label workflow:
 *  1. Loads recipes + recent labels for the active site.
 *  2. Manages the in-flight form state (product, batch, allergens, …).
 *  3. Saves to the backend then optionally fires either the browser
 *     print dialog or the network ESC/POS thermal printer (via the
 *     local Kitchen Bridge LAN bridge first, with a cloud fallback).
 *
 * UI is split into <LabelForm />, <LabelPreview /> and <LabelHistoryList />
 * so this file stays focused on data orchestration.
 */
import { useEffect, useMemo, useState } from "react";
import api, { withSite, formatErr } from "@/lib/api";
import { useSite } from "@/contexts/SiteContext";
import { useAuth } from "@/contexts/AuthContext";
import PageHeader from "@/components/PageHeader";
import { toast } from "sonner";
import LabelForm, { STORAGE_OPTS } from "@/components/labels/LabelForm";
import LabelPreview from "@/components/labels/LabelPreview";
import LabelHistoryList from "@/components/labels/LabelHistoryList";
import { buildLabelGs1Payload, buildLabelVerifyUrl } from "@/lib/labelGs1";
import HelpButton from "@/components/HelpButton";
import WorkflowPrinter from "@/components/workflow/WorkflowPrinter";
import CanvasLabelView from "@/components/workflow/CanvasLabelView";
import { declarationName } from "@/lib/allergens";
import { EMPTY_NUTRITION, formatNipText, cleanNutrition } from "@/lib/nutrition";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { LayoutThumb } from "@/components/workflow/LayoutThumb";
import { logPrint } from "@/lib/printLog";
import { enqueuePrint } from "@/lib/printHub";
import { ChefHat, ShoppingBag, FileText } from "lucide-react";

const WORKFLOW_MODES = [
  { v: "prep", label: "Kitchen Prep", icon: ChefHat },
  { v: "retail", label: "Retail Sale", icon: ShoppingBag },
  { v: "custom", label: "Custom Docket", icon: FileText },
];

function addHoursISO(baseDate, hours) {
  const d = baseDate ? new Date(baseDate) : new Date();
  d.setHours(d.getHours() + Number(hours || 0));
  return d;
}

function initialForm() {
  const now = new Date();
  return {
    product_name: "",
    recipe_id: "",
    batch_number: `B${now.getTime().toString().slice(-5)}`,
    prep_date: new Date().toISOString().slice(0, 16),
    shelf_life_hours: 48,
    use_by_date: "",
    storage: "refrigerated",
    allergens: [],
    quantity: "",
    notes: "",
    price_aud: "",
    best_before: "",
    staff_id: "",
    ingredients: "",
    nutrition: { ...EMPTY_NUTRITION },
    copies: 1,
  };
}

export default function LabelPrinter() {
  const { activeSite } = useSite();
  const { user } = useAuth();
  const canEdit = user?.role !== "auditor";

  const [recipes, setRecipes] = useState([]);
  const [recent, setRecent] = useState([]);
  const [lastLabelId, setLastLabelId] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [realSize, setRealSize] = useState(false);
  const [mode, setMode] = useState("prep");
  const [docketPresetId, setDocketPresetId] = useState("");

  const docketPresets = (activeSite?.canvas_layout_presets || {}).docket || [];
  const docketLayout = docketPresets.find((p) => p.id === docketPresetId)?.layout || null;

  const load = async () => {
    if (!activeSite) return;
    const [r, l] = await Promise.all([
      api.get("/recipes", withSite(activeSite.id)),
      api.get("/labels", withSite(activeSite.id)),
    ]);
    setRecipes(r.data);
    setRecent(l.data.slice(0, 10));
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSite]);

  // auto-derive use_by from prep + shelf life
  useEffect(() => {
    const d = addHoursISO(form.prep_date, form.shelf_life_hours);
    setForm((f) => ({ ...f, use_by_date: d.toISOString().slice(0, 16) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.prep_date, form.shelf_life_hours]);

  const applyRecipe = (rid) => {
    const r = recipes.find((x) => x.id === rid);
    if (!r) return;
    setForm((f) => ({
      ...f,
      recipe_id: rid,
      product_name: r.name,
      allergens: r.allergens || [],
      ingredients: r.ingredients || "",
      nutrition: r.nutrition ? { ...EMPTY_NUTRITION, ...r.nutrition } : { ...EMPTY_NUTRITION },
      shelf_life_hours: r.shelf_life_hours || 48,
    }));
  };

  // Per-product docket defaults (price + staff id) remembered in the browser.
  const defaultsKey = (name) => `docket_defaults:${activeSite?.id || "x"}:${(name || "").trim().toLowerCase()}`;
  const saveDocketDefaults = () => {
    if (!form.product_name) return;
    try {
      localStorage.setItem(defaultsKey(form.product_name), JSON.stringify({
        price_aud: form.price_aud, staff_id: form.staff_id,
      }));
    } catch { /* ignore quota */ }
  };
  // Prefill remembered price/staff when the product name matches — without
  // clobbering anything the user has already typed.
  useEffect(() => {
    if (!form.product_name) return;
    let saved;
    try { saved = JSON.parse(localStorage.getItem(defaultsKey(form.product_name)) || "null"); } catch { saved = null; }
    if (!saved) return;
    setForm((f) => {
      const next = { ...f };
      if (!f.price_aud && saved.price_aud) next.price_aud = saved.price_aud;
      if (!f.staff_id && saved.staff_id) next.staff_id = saved.staff_id;
      return (next.price_aud === f.price_aud && next.staff_id === f.staff_id) ? f : next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.product_name, activeSite?.id]);

  const toggleAllergen = (id) => {
    setForm((f) => {
      const has = f.allergens.includes(id);
      return { ...f, allergens: has ? f.allergens.filter((x) => x !== id) : [...f.allergens, id] };
    });
  };

  const previewLabel = useMemo(() => {
    const prep = form.prep_date ? new Date(form.prep_date) : new Date();
    const ub = form.use_by_date ? new Date(form.use_by_date) : new Date();
    const fmt = (d) =>
      d.toLocaleString("en-AU", {
        day: "2-digit", month: "2-digit", year: "2-digit",
        hour: "2-digit", minute: "2-digit", hour12: false,
      });
    return {
      product: form.product_name || "—",
      batch: form.batch_number || "—",
      prep: fmt(prep),
      useby: fmt(ub),
      storage: STORAGE_OPTS.find((s) => s.v === form.storage)?.l || form.storage,
      allergens: form.allergens,
      quantity: form.quantity,
      site: activeSite?.name || "",
      staff: user?.name || "",
      notes: form.notes,
      gs1Payload: buildLabelGs1Payload(form),
      // Public verify URL — encoded into the printed QR so any phone
      // camera can open the audit-trail page in one tap. Includes the
      // saved label id when we have it; falls back to the batch query
      // string until the user presses Save.
      verifyUrl: buildLabelVerifyUrl(
        { ...form, id: lastLabelId },
        process.env.REACT_APP_BACKEND_URL || "",
      ),
    };
  }, [form, activeSite, user, lastLabelId]);

  // Data map for the on-screen canvas preview when a saved docket template
  // is selected (keys mirror the backend _canvas_value resolver).
  const docketCanvasData = useMemo(() => {
    const al = (form.allergens || []).map(declarationName);
    const priceNum = form.price_aud !== "" && form.price_aud != null ? Number(form.price_aud) : null;
    const bb = form.best_before ? new Date(form.best_before).toLocaleDateString("en-AU", { day: "2-digit", month: "2-digit", year: "numeric" }) : "";
    return {
      site_name: activeSite?.name || "",
      product_name: form.product_name || "",
      quantity: form.quantity || "",
      price: priceNum != null && !Number.isNaN(priceNum) ? `$${priceNum.toFixed(2)}` : "",
      prep_date: previewLabel.prep,
      use_by_date: previewLabel.useby,
      best_before: bb,
      batch_number: form.batch_number || "",
      storage: STORAGE_OPTS.find((s) => s.v === form.storage)?.l || form.storage || "",
      user_name: user?.name || "",
      staff_id: form.staff_id || user?.name || "",
      ingredients: form.ingredients || "",
      nutrition: formatNipText(form.nutrition),
      notes: form.notes || "",
      allergens: (form.allergens || []).map((a) => String(a).toUpperCase()).join(", "),
      allergen_declaration: al.length ? `Contains: ${al.join(", ")}.` : "",
      qr_url: previewLabel.verifyUrl,
      barcode: "",
    };
  }, [form, activeSite, user, previewLabel]);

  const save = async () => {
    if (!form.product_name) {
      toast.error("Product name required");
      return null;
    }
    try {
      const { data } = await api.post("/labels",
        {
          product_name: form.product_name,
          recipe_id: form.recipe_id || null,
          batch_number: form.batch_number,
          prep_date: form.prep_date,
          use_by_date: form.use_by_date,
          storage: form.storage,
          allergens: form.allergens,
          quantity: form.quantity,
          notes: form.notes,
        },
        withSite(activeSite.id));
      setLastLabelId(data.id);
      toast.success("Label saved to history");
      load();
      return data.id;
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
      return null;
    }
  };

  const queueToHub = async () => {
    // "Batch print" — save the label to history, then enqueue a 60×50
    // docket to the Print Hub. The master station renders + prints it,
    // so this works from any device (incl. iPhones with no WebUSB).
    if (!form.product_name) {
      toast.error("Product name required");
      return;
    }
    const id = await save();
    const qrUrl = buildLabelVerifyUrl(
      { ...form, id: id || lastLabelId },
      process.env.REACT_APP_BACKEND_URL || "",
    );
    const priceNum = form.price_aud !== "" && form.price_aud != null ? Number(form.price_aud) : null;
    const payload = {
      label_type: "docket",
      product_name: form.product_name,
      quantity: form.quantity,
      prep_date: previewLabel.prep,
      use_by_date: previewLabel.useby,
      batch_number: form.batch_number,
      storage: form.storage,
      user_name: user?.name || "",
      allergens: form.allergens,
      notes: form.notes,
      price_aud: priceNum != null && !Number.isNaN(priceNum) ? priceNum : undefined,
      best_before: form.best_before || undefined,
      ingredients: form.ingredients || undefined,
      staff_id: form.staff_id || undefined,
      nutrition: form.nutrition?.enabled ? cleanNutrition(form.nutrition) : undefined,
      qr_url: qrUrl,
      canvas_preset_id: docketPresetId || undefined,
      copies: form.copies || 1,
    };
    try {
      const copies = form.copies || 1;
      await enqueuePrint(activeSite.id, {
        label_type: "docket", product_name: form.product_name, copies, payload,
      });
      logPrint(activeSite?.id, {
        product_name: form.product_name, label_type: "custom", method: "tecor", copies, payload,
      });
      saveDocketDefaults();
      toast.success(`Sent ${form.product_name}${copies > 1 ? ` ×${copies}` : ""} to Print Hub`);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message || "Couldn't queue label");
    }
  };

  return (
    <div className="animate-fade-in" data-testid="labels-page">
      <PageHeader
        title="Food Label Printer"
        subtitle="Print 60×50mm Prep & Retail labels on the Tecor TR-422B (WebUSB), or custom 80mm docket labels."
        action={<HelpButton topic="labels" />}
      />

      {/* Workflow mode toggle — Kitchen Prep / Retail Sale / Custom */}
      <div
        className="inline-flex flex-wrap gap-1 p-1 rounded-xl bg-eucalyptus-100 mb-6 no-print"
        data-testid="workflow-mode-toggle"
        role="tablist"
      >
        {WORKFLOW_MODES.map(({ v, label, icon: Icon }) => (
          <button
            key={v}
            type="button"
            role="tab"
            aria-selected={mode === v}
            data-testid={`workflow-mode-${v}`}
            onClick={() => setMode(v)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
              mode === v
                ? "bg-eucalyptus-900 text-white shadow-sm"
                : "text-eucalyptus-900 hover:bg-eucalyptus-200"
            }`}
          >
            <Icon className="w-4 h-4" /> {label}
          </button>
        ))}
      </div>

      {mode !== "custom" ? (
        <WorkflowPrinter mode={mode} activeSite={activeSite} user={user} />
      ) : (
      <div className="space-y-4">
        {/* Saved docket template picker — prints the WYSIWYG canvas design */}
        <div className="surface p-4 flex flex-wrap items-center gap-3 no-print" data-testid="docket-template-picker">
          <div className="min-w-[240px]">
            <label className="label-eyebrow block mb-1">Docket template</label>
            <Select value={docketPresetId || "__none__"} onValueChange={(v) => setDocketPresetId(v === "__none__" ? "" : v)}>
              <SelectTrigger data-testid="docket-template-select" className="h-10">
                <SelectValue placeholder={docketPresets.length ? "Choose a saved template…" : "No saved templates yet"} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__" data-testid="docket-template-opt-none">Default docket (no template)</SelectItem>
                {docketPresets.map((p) => (
                  <SelectItem key={p.id} value={p.id} data-testid={`docket-template-opt-${p.id}`}>
                    <span className="flex items-center gap-2">
                      <LayoutThumb layout={p.layout} />
                      {p.name}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <p className="text-xs text-charcoal-soft max-w-md">
            Design templates in Product Library → Canvas Designer → Custom Docket. Selecting one prints that exact layout to the Print Hub.
          </p>
        </div>

      <div className="grid lg:grid-cols-5 gap-6">
        {canEdit && (
          <LabelForm
            form={form}
            setForm={setForm}
            recipes={recipes}
            onApplyRecipe={applyRecipe}
            onToggleAllergen={toggleAllergen}
            onSave={save}
            onPrint={queueToHub}
          />
        )}
        <div className="lg:col-span-2">
          {docketLayout ? (
            <>
              <div className="label-eyebrow mb-3 no-print" data-testid="docket-canvas-eyebrow">Custom template preview</div>
              <div data-testid="docket-canvas-preview">
                <CanvasLabelView layout={docketLayout} data={docketCanvasData} />
              </div>
              <LabelHistoryList labels={recent} />
            </>
          ) : (
            <>
              <div className="flex items-center justify-between mb-3 no-print">
                <div className="label-eyebrow">
                  {realSize
                    ? `Real size · ${activeSite?.label_width_mm || 80}mm × ${activeSite?.label_height_mm || "auto"}`
                    : "80mm thermal docket preview"}
                </div>
                <label className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest cursor-pointer select-none">
                  <input
                    type="checkbox"
                    data-testid="lp-real-size"
                    checked={realSize}
                    onChange={(e) => setRealSize(e.target.checked)}
                    className="accent-eucalyptus-900"
                  />
                  Show at real size
                </label>
              </div>
              <LabelPreview label={previewLabel} realSize={realSize} widthMm={activeSite?.label_width_mm} heightMm={activeSite?.label_height_mm} />
              <LabelHistoryList labels={recent} />
            </>
          )}
        </div>
      </div>
      </div>
      )}
    </div>
  );
}
